import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, TrendingUp, Calculator, FileText, Users, Shield, Mail } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import WageAtlasLogo from '@/components/layout/WageAtlasLogo';
import NewsletterSignup from '@/components/shared/NewsletterSignup';
import { BRAND_OPERATOR_LINE, COMPANY } from '@/lib/companyInfo';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: 'Salary Research',
      icon: TrendingUp,
      links: [
        { label: 'Occupation Salaries', href: '/salary/software-developer/california/' },
        { label: 'All Occupations', href: '/occupations/' },
        { label: 'Salaries by State', href: '/states/' },
        { label: 'Industry Analysis', href: '/industry/' },
        { label: 'Salary Trends', href: '/trends/' },
        { label: 'Best States', href: '/best-states/' },
        { label: 'State Rankings', href: '/state/california/' },
        { label: 'Best Paying Jobs', href: '/highest-paying/jobs/' },
      ]
    },
    {
      title: 'Career Tools',
      icon: Calculator,
      links: [
        { label: 'Am I Underpaid?', href: '/am-i-underpaid/' },
        { label: 'Income Percentile', href: '/income-percentile/' },
        { label: 'Salary Calculator', href: '/calculators/salary/' },
        { label: 'Tax Calculator', href: '/calculators/tax/' },
        { label: 'In-Demand Careers', href: '/in-demand-careers/' },
        { label: 'AI Economy Jobs', href: '/ai-economy-jobs/' },
        { label: 'AI Career Analysis', href: '/ai-career-analysis/' }
      ]
    },
    {
      title: 'Resources',
      icon: FileText,
      links: [
        { label: 'Real Wages Explained', href: '/real-wages/' },
        { label: 'SOC Code Guide', href: '/what-is-soc-code/' },
        { label: 'Methodology', href: '/methodology/' },
        { label: 'Data Sources', href: '/data-sources/' },
        { label: 'Career Insights', href: '/insights/' }
      ]
    },
    {
      title: 'Company',
      icon: Users,
      links: [
        { label: 'About Us', href: '/about/' },
        { label: 'Privacy Policy', href: '/privacy/' },
        { label: 'Terms of Service', href: '/terms/' },
        { label: 'Refund Policy', href: '/refund-policy/' },
        { label: 'Contact', href: '/contact/' },
        { label: 'Support', href: '/support/' }
      ]
    }
  ];

  const popularStates = [
    'California', 'Texas', 'Florida', 'New York', 'Pennsylvania',
    'Illinois', 'Ohio', 'Georgia', 'North Carolina', 'Michigan'
  ];

  const popularOccupations = [
    { label: 'Software Developers', slug: 'software-developers' },
    { label: 'Registered Nurses', slug: 'registered-nurses' },
    { label: 'Accountants & Auditors', slug: 'accountants-and-auditors' },
    { label: 'Financial Managers', slug: 'financial-managers' },
    { label: 'Electricians', slug: 'electricians' },
    { label: 'General & Operations Managers', slug: 'general-and-operations-managers' },
    { label: 'Lawyers', slug: 'lawyers' },
    { label: 'Truck Drivers', slug: 'heavy-and-tractor-trailer-truck-drivers' },
    { label: 'Teachers', slug: 'elementary-and-middle-school-teachers' },
    { label: 'Police Officers', slug: 'police-and-sheriffs-patrol-officers' },
  ];

  return (
    <footer className="bg-background border-t mt-16">
      <div className="container mx-auto px-4 py-12">
        {/* Brand */}
        <div className="mb-10 max-w-md">
          <Link to="/" className="inline-flex items-center" aria-label="WageAtlas home">
            <WageAtlasLogo size="lg" />
          </Link>
          <p className="mt-4 text-sm text-muted-foreground">
            Salary data, career insights, and wage trends from official U.S. Bureau of Labor Statistics data.
          </p>
        </div>

        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {footerSections.map((section) => {
            const IconComponent = section.icon;
            return (
              <div key={section.title} className="space-y-4">
                <div className="flex items-center gap-2">
                  <IconComponent className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground">{section.title}</h3>
                </div>
                <ul className="space-y-2">
                  {section.links.map((link) => (
                    <li key={link.href}>
                      <Link 
                        to={link.href}
                        className="text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <Separator className="my-8" />

        {/* Popular Links Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-4 w-4 text-primary" />
              <h4 className="font-medium text-foreground">Popular States</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {popularStates.map((state) => (
                <Link
                  key={state}
                  to={`/state/${state.toLowerCase().replace(/\s+/g, '-')}/`}
                  className="text-xs px-2 py-1 bg-muted hover:bg-muted/80 rounded-md text-muted-foreground hover:text-primary transition-colors"
                >
                  {state}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-4 w-4 text-primary" />
              <h4 className="font-medium text-foreground">Popular Careers</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {popularOccupations.map((occ) => (
                <Link
                  key={occ.slug}
                  to={`/salary/${occ.slug}/united-states/`}
                  className="text-xs px-2 py-1 bg-muted hover:bg-muted/80 rounded-md text-muted-foreground hover:text-primary transition-colors"
                >
                  {occ.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <Separator className="my-8" />

        {/* Newsletter Signup */}
        <div className="max-w-xl mx-auto mb-8">
          <NewsletterSignup
            source="footer"
            variant="compact"
            className="border-primary/20 bg-primary/5"
          />
        </div>

        <Separator className="my-8" />

        {/* Trust / support utilities */}
        <div className="flex flex-wrap items-center justify-center gap-4 text-center">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            <span className="text-xs text-muted-foreground">Secure & Private</span>
          </div>
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" />
            <Link
              to="/contact/"
              className="text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              Contact Support
            </Link>
          </div>
        </div>

        {/* Legal notices — one block, no restated operator/SEO copy */}
        <div className="mt-6 pt-4 border-t space-y-3 text-center text-xs text-muted-foreground max-w-4xl mx-auto">
          <p>
            <strong>Disclosure:</strong> Some links are affiliates; we may earn a commission
            if you purchase or sign up through them, at no extra cost to you.
          </p>
          <p>
            <strong>Disclaimer:</strong> Not affiliated with, endorsed by, or sponsored by the
            U.S. Government, the U.S. Bureau of Labor Statistics, or any government agency.
            Data is provided &quot;as-is&quot; without warranties.{' '}
            <Link to="/terms/" className="text-primary hover:underline">
              Terms of Service
            </Link>
          </p>
        </div>

        {/* Copyright / parent company / attribution — last */}
        <div className="mt-8 pt-4 border-t text-center text-sm text-muted-foreground">
          <p>© {currentYear} {COMPANY.legalName}. All rights reserved.</p>
          <p className="mt-1">{BRAND_OPERATOR_LINE}</p>
          <p className="mt-1">Salary data powered by U.S. Bureau of Labor Statistics</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;